import React from 'react';
import {
  Users,
  Clock,
  Sparkles,
  TrendingUp,
  Flame,
  Star,
  Globe,
  Lock,
  Eye,
  UserPlus,
  MessageCircle,
  Hash,
  Settings,
  ArrowLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Plus,
  Filter,
  RefreshCw,
  Send,
  Paperclip,
  Smile,
  FileText,
  Search,
  MoreHorizontal,
  Award,
  Shield,
  Bell,
  Bookmark,
  Share2,
  Heart,
  MessageSquare,
  Calendar,
  Activity,
  Zap,
  Pin,
  Image,
  Link2,
  ThumbsUp,
  ExternalLink,
  ChevronLeft,
  BarChart3,
  PieChart,
  TrendingDown,
  UserCheck,
  UserMinus,
  Info,
  Menu,
  BookOpen,
  Home,
  Compass,
} from 'lucide-react';
import './CommunitiesMockupContent.css';

const CommunitiesMockupContent = ({ 
  visibleCards = 0, 
  animationPhase,
  showInterior = false,
  visibleItems = 0,
  activeInteriorTab = 'feed', // 'feed', 'threads', 'members', 'chat', 'about', 'analytics'
  showCommunityDashboard = false, // true when inside a specific community
  onTabClick = () => {}, // Handler for tab clicks
}) => {

  // ========================================
  // UNIFIED FEED DATA (Cross-community feed)
  // ========================================
  const unifiedFeedPosts = [
    {
      id: 1,
      community: { name: 'AI/ML Research', avatar: 'AI', color: 'indigo', members: '12.5K' },
      author: { name: 'Dr. Lena Hoffmann', avatar: 'LH', role: 'creator', verified: true },
      title: 'New breakthrough in transformer efficiency - 40% reduction in compute',
      content: 'We just published our findings on sparse attention mechanisms...',
      tags: ['research', 'transformers', 'announcement'],
      upvotes: 847,
      downvotes: 12,
      comments: 156,
      views: '3.2K',
      timeAgo: '2h',
      isHot: true,
      isPinned: false,
      awards: [{ type: 'gold', count: 3 }, { type: 'insight', count: 5 }],
      hasImage: true,
    },
    {
      id: 2,
      community: { name: 'Quantum Computing', avatar: 'QC', color: 'cyan', members: '8.3K' },
      author: { name: 'Farid Anwar', avatar: 'FA', role: 'moderator', verified: false },
      title: 'Question: Best resources for learning quantum error correction?',
      content: 'I have a solid background in classical CS, looking to dive deep into QEC...',
      tags: ['question', 'learning', 'error-correction'],
      upvotes: 234,
      downvotes: 3,
      comments: 45,
      views: '1.1K',
      timeAgo: '4h',
      isHot: false,
      isPinned: false,
      awards: [{ type: 'helpful', count: 2 }],
      hasImage: false,
    },
    {
      id: 3,
      community: { name: 'Bioinformatics', avatar: 'BI', color: 'green', members: '6.7K' },
      author: { name: 'Ingrid Svensson', avatar: 'IS', role: 'member', verified: true },
      title: '[Collaboration] Looking for co-authors on protein folding paper',
      content: 'Our lab has promising results on AlphaFold extensions, seeking ML expertise...',
      tags: ['collaboration', 'proteins', 'alphafold'],
      upvotes: 412,
      downvotes: 8,
      comments: 67,
      views: '2.4K',
      timeAgo: '6h',
      isHot: true,
      isTrending: true,
      isPinned: false,
      awards: [{ type: 'collaboration', count: 1 }],
      hasImage: false,
    },
    {
      id: 4,
      community: { name: 'Climate Science', avatar: 'CS', color: 'emerald', members: '9.1K' },
      author: { name: 'Prof. Idris Malik', avatar: 'IM', role: 'creator', verified: true },
      title: '📊 Weekly Data Drop: Arctic ice measurements Jan 2026',
      content: 'This week\'s satellite data shows continued concerning trends...',
      tags: ['data', 'arctic', 'weekly'],
      upvotes: 623,
      downvotes: 15,
      comments: 89,
      views: '4.1K',
      timeAgo: '8h',
      isHot: false,
      isPinned: true,
      awards: [{ type: 'data', count: 4 }],
      hasImage: true,
    },
    {
      id: 5,
      community: { name: 'AI/ML Research', avatar: 'AI', color: 'indigo', members: '12.5K' },
      author: { name: 'Owen Gallagher', avatar: 'OG', role: 'member', verified: false },
      title: 'Discussion: Ethics of using LLMs for peer review assistance',
      content: 'What are the community thoughts on using Claude/GPT for initial paper reviews?',
      tags: ['discussion', 'ethics', 'llm'],
      upvotes: 567,
      downvotes: 89,
      comments: 203,
      views: '5.8K',
      timeAgo: '12h',
      isHot: true,
      isControversial: true,
      isPinned: false,
      awards: [],
      hasImage: false,
    },
  ];

  // ========================================
  // COMMUNITIES DISCOVERY DATA
  // ========================================
  const communitiesData = [
    {
      id: 1,
      name: 'AI/ML Research',
      description: 'Cutting-edge AI and machine learning research discussions, paper reviews, and collaborations.',
      avatar: 'AI',
      coverGradient: 'indigo',
      type: 'public',
      field: 'Computer Science',
      subFields: ['Deep Learning', 'NLP', 'Computer Vision'],
      members: 12547,
      online: 342,
      threads: 3420,
      createdAt: 'Mar 2023',
      activity: 'Very Active',
      growth: '+12%',
      match: 94,
    },
    {
      id: 2,
      name: 'Quantum Computing',
      description: 'Exploring quantum algorithms, hardware, and the future of computation.',
      avatar: 'QC',
      coverGradient: 'cyan',
      type: 'public',
      field: 'Physics',
      subFields: ['Quantum Algorithms', 'Error Correction'],
      members: 8324,
      online: 156,
      threads: 1890,
      createdAt: 'Jun 2023',
      activity: 'Active',
      growth: '+8%',
      match: 76,
    },
    {
      id: 3,
      name: 'Bioinformatics Hub',
      description: 'Computational biology, genomics, and data-driven life sciences.',
      avatar: 'BI',
      coverGradient: 'green',
      type: 'public',
      field: 'Biology',
      subFields: ['Genomics', 'Proteomics'],
      members: 6721,
      online: 98,
      threads: 1456,
      createdAt: 'Sep 2023',
      activity: 'Active',
      growth: '+15%',
      match: 82,
    },
    {
      id: 4,
      name: 'Climate Modeling',
      description: 'Advanced climate models, data analysis, and environmental research.',
      avatar: 'CM',
      coverGradient: 'emerald',
      type: 'private',
      field: 'Environmental Science',
      subFields: ['Modeling', 'Data Science'],
      members: 4532,
      online: 67,
      threads: 892,
      createdAt: 'Jan 2024',
      activity: 'Moderate',
      growth: '+6%',
      match: 68,
    },
    {
      id: 5,
      name: 'Neuroscience Network',
      description: 'Brain research, cognitive science, and neural engineering discussions.',
      avatar: 'NN',
      coverGradient: 'pink',
      type: 'public',
      field: 'Neuroscience',
      subFields: ['Cognitive Science', 'Neural Engineering'],
      members: 7891,
      online: 123,
      threads: 2134,
      createdAt: 'Apr 2023',
      activity: 'Very Active',
      growth: '+10%',
      match: 71,
    },
    {
      id: 6,
      name: 'Cryptography Research',
      description: 'Post-quantum cryptography, zero-knowledge proofs, and security research.',
      avatar: 'CR',
      coverGradient: 'amber',
      type: 'private',
      field: 'Mathematics',
      subFields: ['Post-Quantum', 'ZK Proofs'],
      members: 3245,
      online: 45,
      threads: 678,
      createdAt: 'Aug 2024',
      activity: 'Active',
      growth: '+18%',
      match: 59,
    },
  ];

  // ========================================
  // COMMUNITY INTERIOR - THREADS DATA (Medical Theme)
  // ========================================
  const communityThreads = [
    {
      id: 1,
      title: '🩺 Case Study: Rare Autoimmune Hepatitis in Pediatric Patient',
      author: { name: 'Dr. Lena Hoffmann', avatar: 'LH', role: 'creator' },
      content: '12-year-old female presenting with elevated liver enzymes (ALT 890, AST 720), positive ANA titers. Initial biopsy shows interface hepatitis...',
      tags: ['case-study', 'hepatology', 'pediatric'],
      upvotes: 892,
      comments: 67,
      timeAgo: '2h',
      isPinned: true,
      isLocked: false,
      flair: { text: 'Case Study', color: 'pink' },
      replies: [
        { author: { name: 'Dr. Idris Malik', avatar: 'IM', role: 'moderator' }, content: 'Have you considered overlap syndrome? The ANA pattern might suggest AIH-PBC overlap. Would recommend checking AMA and IgM levels.', upvotes: 156, timeAgo: '1h' },
        { author: { name: 'Dr. Amina Rashid', avatar: 'AR', role: 'member' }, content: 'Agree with Dr. Malik. Also, what was the gamma globulin level? In pediatric AIH, we typically see significant hypergammaglobulinemia.', upvotes: 98, timeAgo: '45m' },
        { author: { name: 'Dr. Lena Hoffmann', avatar: 'LH', role: 'creator' }, content: 'Great points! IgG was 2,800 mg/dL. AMA negative. Starting on prednisolone 1mg/kg with azathioprine taper planned.', upvotes: 72, timeAgo: '30m' },
      ],
    },
    {
      id: 2,
      title: 'Weekly Grand Rounds - Challenging Diagnostic Cases',
      author: { name: 'PathBot', avatar: 'PB', role: 'bot' },
      content: 'Submit your challenging cases for this week\'s virtual grand rounds. Focus: Hematopathology and bone marrow interpretation...',
      tags: ['grand-rounds', 'weekly', 'hematology'],
      upvotes: 634,
      comments: 189,
      timeAgo: '6h',
      isPinned: true,
      isLocked: false,
      flair: { text: 'Grand Rounds', color: 'blue' },
      replies: [
        { author: { name: 'Dr. Farid Anwar', avatar: 'FA', role: 'moderator' }, content: 'Submitting a fascinating MDS case with unusual cytogenetics - del(5q) with concurrent SF3B1 mutation. Will post images shortly.', upvotes: 89, timeAgo: '5h' },
        { author: { name: 'Dr. Ingrid Svensson', avatar: 'IS', role: 'member' }, content: 'Looking forward to it! I have a plasma cell neoplasm with atypical morphology - CD138+/CD56-/cyclinD1+. FISH pending.', upvotes: 67, timeAgo: '4h' },
      ],
    },
    {
      id: 3,
      title: '📋 New WHO 2025 Classification: Key Changes for Lymphoma',
      author: { name: 'Dr. Farid Anwar', avatar: 'FA', role: 'moderator' },
      content: 'Summary of major updates in the new WHO classification affecting B-cell lymphomas. Notable changes include reclassification of...',
      tags: ['guidelines', 'lymphoma', 'WHO-2025'],
      upvotes: 1247,
      comments: 156,
      timeAgo: '1d',
      isPinned: false,
      isLocked: false,
      flair: { text: 'Guidelines', color: 'indigo' },
      replies: [
        { author: { name: 'Dr. Owen Gallagher', avatar: 'OG', role: 'member' }, content: 'The DLBCL molecular subtyping requirement is going to be challenging for smaller labs. Any thoughts on practical implementation?', upvotes: 134, timeAgo: '20h' },
        { author: { name: 'Dr. Margot Dupont', avatar: 'MD', role: 'member' }, content: 'We\'re partnering with a reference lab for NanoString. Turnaround is ~5 days but better than nothing.', upvotes: 112, timeAgo: '18h' },
        { author: { name: 'Dr. Farid Anwar', avatar: 'FA', role: 'moderator' }, content: 'Good discussion! I\'ll compile a resource guide for implementing the new requirements. Stay tuned.', upvotes: 89, timeAgo: '12h' },
      ],
    },
    {
      id: 4,
      title: 'Question: Interpreting PD-L1 in NSCLC with Necrosis',
      author: { name: 'Dr. Soren Lindqvist', avatar: 'SL', role: 'member' },
      content: 'How do you handle PD-L1 scoring when >50% of the tumor shows necrosis? Patient needs treatment decision urgently...',
      tags: ['question', 'immunotherapy', 'lung'],
      upvotes: 445,
      comments: 78,
      timeAgo: '4h',
      isPinned: false,
      isLocked: false,
      flair: { text: 'Question', color: 'cyan' },
      replies: [
        { author: { name: 'Dr. Lena Hoffmann', avatar: 'LH', role: 'creator' }, content: 'Score only viable tumor cells. If <100 viable cells, note it as "insufficient for reliable scoring" and request rebiopsy if clinically feasible.', upvotes: 167, timeAgo: '3h' },
        { author: { name: 'Dr. Idris Malik', avatar: 'IM', role: 'moderator' }, content: 'Agree. Also document the % viable tumor in your report. Some oncologists will proceed with combo therapy if PD-L1 is borderline.', upvotes: 145, timeAgo: '2h' },
      ],
    },
    {
      id: 5,
      title: 'Research Collaboration: Multi-center Thyroid Cytology AI Study',
      author: { name: 'Dr. Ingrid Svensson', avatar: 'IS', role: 'member' },
      content: 'Seeking collaborators for validation study of AI-assisted thyroid FNA interpretation. Need sites with >500 cases/year...',
      tags: ['collaboration', 'AI', 'cytology', 'thyroid'],
      upvotes: 567,
      comments: 45,
      timeAgo: '8h',
      isPinned: false,
      isLocked: false,
      flair: { text: 'Collaboration', color: 'green' },
      replies: [
        { author: { name: 'Dr. Amina Rashid', avatar: 'AR', role: 'member' }, content: 'Our institution does ~800 thyroid FNAs annually. Very interested! What\'s the IRB status?', upvotes: 78, timeAgo: '6h' },
        { author: { name: 'Dr. Owen Gallagher', avatar: 'OG', role: 'member' }, content: 'Count us in - we have a strong cytology program. DM sent with details.', upvotes: 56, timeAgo: '5h' },
      ],
    },
    {
      id: 6,
      title: '🔬 Interesting Finding: Incidental Amyloid in Cardiac Biopsy',
      author: { name: 'Dr. Margot Dupont', avatar: 'MD', role: 'member' },
      content: 'Routine EMB for transplant surveillance revealed unexpected Congo red positivity. Patient asymptomatic. How aggressive to workup?',
      tags: ['case-study', 'cardiac', 'amyloid'],
      upvotes: 723,
      comments: 92,
      timeAgo: '10h',
      isPinned: false,
      isLocked: false,
      flair: { text: 'Case Study', color: 'pink' },
      replies: [
        { author: { name: 'Dr. Farid Anwar', avatar: 'FA', role: 'moderator' }, content: 'Definitely needs typing! ATTR vs AL has huge prognostic implications. Mass spec if available, otherwise IHC panel.', upvotes: 189, timeAgo: '9h' },
        { author: { name: 'Dr. Idris Malik', avatar: 'IM', role: 'moderator' }, content: 'Also check serum free light chains and SPEP/UPEP. If ATTR, consider genetic testing for hereditary forms.', upvotes: 156, timeAgo: '8h' },
        { author: { name: 'Dr. Margot Dupont', avatar: 'MD', role: 'member' }, content: 'Mass spec confirmed ATTR! Genetic testing pending. Thanks for the guidance everyone.', upvotes: 234, timeAgo: '3h' },
      ],
    },
  ];

  // ========================================
  // COMMUNITY MEMBERS DATA
  // ========================================
  const membersData = {
    stats: {
      total: 12547,
      online: 342,
      newThisWeek: 89,
      moderators: 8,
    },
    creators: [
      { name: 'Dr. Lena Hoffmann', avatar: 'LH', role: 'creator', joinedDate: 'Mar 2023', posts: 456, reputation: 15420 },
    ],
    moderators: [
      { name: 'Farid Anwar', avatar: 'FA', role: 'moderator', joinedDate: 'Apr 2023', posts: 234, reputation: 8920 },
      { name: 'Amina Rashid', avatar: 'AR', role: 'moderator', joinedDate: 'May 2023', posts: 189, reputation: 7650 },
      { name: 'Idris Malik', avatar: 'IM', role: 'moderator', joinedDate: 'Jun 2023', posts: 167, reputation: 6890 },
    ],
    topContributors: [
      { name: 'Ingrid Svensson', avatar: 'IS', role: 'member', posts: 312, reputation: 9870, badge: 'Top Contributor' },
      { name: 'Owen Gallagher', avatar: 'OG', role: 'member', posts: 278, reputation: 8540, badge: 'Rising Star' },
      { name: 'Soren Lindqvist', avatar: 'SL', role: 'member', posts: 245, reputation: 7230, badge: 'Helpful' },
      { name: 'Margot Dupont', avatar: 'MD', role: 'member', posts: 198, reputation: 6120, badge: 'Active' },
    ],
  };

  // ========================================
  // COMMUNITY CHAT DATA
  // ========================================
  const chatMessages = [
    {
      id: 1,
      user: { name: 'Lena Hoffmann', avatar: 'LH', role: 'creator' },
      message: "Welcome to the AI/ML Research community chat! 🎉 Feel free to discuss anything research-related here.",
      time: '9:30 AM',
      isMe: false,
      isPinned: true,
    },
    {
      id: 2,
      user: { name: 'Farid Anwar', avatar: 'FA', role: 'moderator' },
      message: "Just published the new paper discussion thread. Check it out!",
      time: '10:15 AM',
      isMe: false,
      reactions: [{ emoji: '👍', count: 5 }, { emoji: '🔥', count: 3 }],
    },
    {
      id: 3,
      user: { name: 'You', avatar: 'MA', role: 'member' },
      message: "Thanks for the heads up! The transformer efficiency paper looks really promising.",
      time: '10:22 AM',
      isMe: true,
    },
    {
      id: 4,
      user: { name: 'Ingrid Svensson', avatar: 'IS', role: 'member' },
      message: "Anyone interested in collaborating on replicating the results? I have compute access.",
      time: '10:45 AM',
      isMe: false,
      reactions: [{ emoji: '🙋', count: 8 }],
    },
    {
      id: 5,
      user: { name: 'Owen Gallagher', avatar: 'OG', role: 'member' },
      message: "Count me in! What framework are they using?",
      time: '10:52 AM',
      isMe: false,
    },
  ];

  // ========================================
  // COMMUNITY ANALYTICS DATA
  // ========================================
  const analyticsData = {
    overview: [
      { label: 'Total Members', value: '12,547', change: '+342', trend: 'up', icon: Users },
      { label: 'Active Today', value: '1,892', change: '+12%', trend: 'up', icon: Activity },
      { label: 'New Threads', value: '156', change: '+8', trend: 'up', icon: MessageSquare },
      { label: 'Engagement', value: '78%', change: '+5%', trend: 'up', icon: TrendingUp },
    ],
    growth: [
      { day: 'Mon', members: 45 },
      { day: 'Tue', members: 62 },
      { day: 'Wed', members: 38 },
      { day: 'Thu', members: 71 },
      { day: 'Fri', members: 56 },
      { day: 'Sat', members: 34 },
      { day: 'Sun', members: 28 },
    ],
    topPosts: [
      { title: 'Transformer efficiency breakthrough', views: 3200, engagement: '94%' },
      { title: 'Weekly Q&A Thread', views: 2800, engagement: '89%' },
      { title: 'Collaboration: LLM benchmarks', views: 2100, engagement: '82%' },
    ],
  };

  // ========================================
  // ABOUT/INFO DATA
  // ========================================
  const communityAbout = {
    name: 'AI/ML Research',
    description: 'A community dedicated to cutting-edge artificial intelligence and machine learning research. We discuss papers, share insights, and collaborate on projects.',
    rules: [
      { id: 1, title: 'Be Respectful', desc: 'Treat all members with respect and courtesy.' },
      { id: 2, title: 'Stay On Topic', desc: 'Keep discussions related to AI/ML research.' },
      { id: 3, title: 'No Self-Promotion', desc: 'Limit promotional content to designated threads.' },
      { id: 4, title: 'Cite Sources', desc: 'Always provide sources for claims and research.' },
    ],
    links: [
      { label: 'Discord Server', url: '#', icon: MessageCircle },
      { label: 'GitHub Org', url: '#', icon: Link2 },
      { label: 'Research Wiki', url: '#', icon: BookOpen },
    ],
    created: 'March 2023',
    category: 'Computer Science',
  };

  // ========================================
  // RENDER: Unified Feed View
  // ========================================
  const renderUnifiedFeed = () => (
    <div className="comm-unified-feed">
      {/* Feed Header */}
      <div className="comm-feed-header">
        <div className="comm-feed-tabs">
          <button className="comm-feed-tab active">
            <Home size={12} />
            <span>Home</span>
          </button>
          <button className="comm-feed-tab">
            <Compass size={12} />
            <span>Discover</span>
          </button>
          <button className="comm-feed-tab">
            <Bookmark size={12} />
            <span>Saved</span>
          </button>
        </div>
      </div>

      {/* Sort Bar */}
      <div className="comm-sort-bar">
        <div className="comm-sort-options">
          <button className="comm-sort-btn active">
            <Flame size={10} />
            <span>Hot</span>
          </button>
          <button className="comm-sort-btn">
            <Star size={10} />
            <span>Best</span>
          </button>
          <button className="comm-sort-btn">
            <TrendingUp size={10} />
            <span>Top</span>
          </button>
          <button className="comm-sort-btn">
            <Clock size={10} />
            <span>New</span>
          </button>
        </div>
        <div className="comm-feed-info">
          <span>Showing posts from 8 communities</span>
        </div>
      </div>

      {/* Feed Posts */}
      <div className="comm-feed-posts">
        {unifiedFeedPosts.map((post, index) => (
          <div 
            key={post.id}
            className={`comm-feed-post ${index < visibleItems ? 'visible' : ''}`}
            style={{ transitionDelay: `${index * 100}ms` }}
          >
            {/* Vote Section */}
            <div className="comm-post-votes">
              <button className="comm-vote-btn up">
                <ChevronUp size={14} />
              </button>
              <span className="comm-vote-score">{post.upvotes}</span>
              <button className="comm-vote-btn down">
                <ChevronDown size={14} />
              </button>
              {post.isHot && (
                <div className="comm-post-badge hot">
                  <Flame size={10} />
                </div>
              )}
              {post.isTrending && (
                <div className="comm-post-badge trending">
                  <TrendingUp size={10} />
                </div>
              )}
            </div>

            {/* Post Content */}
            <div className="comm-post-main">
              {/* Community & Author Info */}
              <div className="comm-post-meta">
                <div className={`comm-post-community comm-community--${post.community.color}`}>
                  <div className="comm-community-avatar">{post.community.avatar}</div>
                  <span className="comm-community-name">{post.community.name}</span>
                </div>
                <span className="comm-post-dot">•</span>
                <span className="comm-post-author">
                  Posted by <strong>{post.author.name}</strong>
                  {post.author.role === 'creator' && (
                    <span className="comm-author-badge creator"><Award size={8} /></span>
                  )}
                  {post.author.role === 'moderator' && (
                    <span className="comm-author-badge mod"><Shield size={8} /></span>
                  )}
                </span>
                <span className="comm-post-dot">•</span>
                <span className="comm-post-time">{post.timeAgo}</span>
              </div>

              {/* Title */}
              <h3 className="comm-post-title">
                {post.isPinned && <Pin size={12} className="comm-pin-icon" />}
                {post.title}
              </h3>

              {/* Content Preview */}
              <p className="comm-post-content">{post.content}</p>

              {/* Tags */}
              <div className="comm-post-tags">
                {post.tags.map((tag, i) => (
                  <span key={i} className="comm-post-tag">
                    <Hash size={9} />
                    {tag}
                  </span>
                ))}
              </div>

              {/* Awards */}
              {post.awards.length > 0 && (
                <div className="comm-post-awards">
                  {post.awards.map((award, i) => (
                    <span key={i} className={`comm-award comm-award--${award.type}`}>
                      <Award size={10} />
                      <span>{award.count}</span>
                    </span>
                  ))}
                </div>
              )}

              {/* Actions */}
              <div className="comm-post-actions">
                <button className="comm-action-btn">
                  <MessageCircle size={12} />
                  <span>{post.comments} Comments</span>
                </button>
                <button className="comm-action-btn">
                  <Share2 size={12} />
                  <span>Share</span>
                </button>
                <button className="comm-action-btn">
                  <Bookmark size={12} />
                  <span>Save</span>
                </button>
                <button className="comm-action-btn">
                  <MoreHorizontal size={12} />
                </button>
              </div>
            </div>

            {/* Thumbnail */}
            {post.hasImage && (
              <div className="comm-post-thumbnail">
                <Image size={20} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  // ========================================
  // RENDER: Discovery View
  // ========================================
  const renderDiscoveryView = () => (
    <div className="comm-discovery">
      {/* Header */}
      <header className="comm-header">
        <div className="comm-header-left">
          <div className="comm-title-row">
            <h2 className="comm-page-title">Communities</h2>
            <div className="comm-title-badge">
              <Users size={10} />
              <span>2.4K Active</span>
            </div>
          </div>
          <p className="comm-page-subtitle">Join research communities worldwide</p>
        </div>
        <button className="comm-create-btn">
          <Plus size={11} />
          <span>Create Community</span>
        </button>
      </header>

      {/* Tabs */}
      <div className="comm-tabs-container">
        <div className="comm-tabs">
          <button className="comm-tab active">
            <Sparkles size={10} />
            <span>For You</span>
          </button>
          <button className="comm-tab">
            <TrendingUp size={10} />
            <span>Trending</span>
          </button>
          <button className="comm-tab">
            <Flame size={10} />
            <span>Hot</span>
          </button>
          <button className="comm-tab">
            <Clock size={10} />
            <span>New</span>
          </button>
          <button className="comm-tab">
            <Users size={10} />
            <span>My Communities</span>
          </button>
        </div>
        <button className="comm-refresh-btn">
          <RefreshCw size={10} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Communities Grid */}
      <div className="comm-grid">
        {communitiesData.map((community, index) => (
          <div 
            key={community.id}
            className={`comm-card comm-card--${community.coverGradient} ${index < visibleCards ? 'visible' : ''}`}
            style={{ transitionDelay: `${index * 100}ms` }}
          >
            {/* Card Header */}
            <div className={`comm-card-cover comm-card-cover--${community.coverGradient}`}>
              <div className="comm-card-avatar">{community.avatar}</div>
              <div className="comm-card-type">
                {community.type === 'public' ? <Globe size={10} /> : <Lock size={10} />}
                <span>{community.type}</span>
              </div>
            </div>

            {/* Card Content */}
            <div className="comm-card-content">
              <h3 className="comm-card-title">{community.name}</h3>
              <p className="comm-card-desc">{community.description}</p>

              {/* Field Tags */}
              <div className="comm-card-fields">
                <span className="comm-card-field">{community.field}</span>
                {community.subFields.slice(0, 1).map((sf, i) => (
                  <span key={i} className="comm-card-subfield">{sf}</span>
                ))}
              </div>

              {/* Stats Row */}
              <div className="comm-card-stats">
                <div className="comm-stat">
                  <Users size={10} />
                  <span>{community.members.toLocaleString()}</span>
                </div>
                <div className="comm-stat online">
                  <span className="comm-online-dot"></span>
                  <span>{community.online} online</span>
                </div>
                <div className="comm-stat">
                  <MessageSquare size={10} />
                  <span>{community.threads}</span>
                </div>
              </div>

              {/* Match & Activity */}
              <div className="comm-card-meta">
                <span className={`comm-match ${community.match >= 80 ? 'high' : community.match >= 60 ? 'medium' : 'low'}`}>
                  {community.match}% Match
                </span>
                <span className="comm-activity">{community.activity}</span>
                <span className="comm-growth">
                  <TrendingUp size={9} />
                  {community.growth}
                </span>
              </div>

              {/* Actions */}
              <div className="comm-card-actions">
                <button className="comm-btn comm-btn--secondary">
                  <Eye size={10} />
                  Preview
                </button>
                <button className="comm-btn comm-btn--primary">
                  <UserPlus size={10} />
                  Join
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // ========================================
  // RENDER: Community Threads View (Full threads with replies)
  // ========================================
  const renderThreadsView = () => (
    <div className="comm-threads">
      {/* Threads Header */}
      <div className="comm-threads-header">
        <div className="comm-threads-search">
          <Search size={11} />
          <input type="text" placeholder="Search discussions..." />
        </div>
        <button className="comm-create-thread-btn">
          <Plus size={10} />
          New Discussion
        </button>
      </div>

      {/* Sort & Filter */}
      <div className="comm-threads-controls">
        <div className="comm-threads-sort">
          <button className="comm-threads-sort-btn active">
            <Flame size={10} />
            Hot
          </button>
          <button className="comm-threads-sort-btn">
            <TrendingUp size={10} />
            Top
          </button>
          <button className="comm-threads-sort-btn">
            <Clock size={10} />
            New
          </button>
        </div>
        <div className="comm-threads-filter">
          <Filter size={10} />
          <span>All Topics</span>
          <ChevronDown size={10} />
        </div>
      </div>

      {/* Threads List with Full Replies */}
      <div className="comm-threads-list">
        {communityThreads.slice(0, 4).map((thread, index) => (
          <div 
            key={thread.id}
            className={`comm-thread-item ${thread.isPinned ? 'pinned' : ''} ${index < visibleItems ? 'visible' : ''}`}
            style={{ transitionDelay: `${index * 80}ms` }}
          >
            {/* Vote Section */}
            <div className="comm-thread-votes">
              <button className="comm-thread-vote-btn">
                <ChevronUp size={14} />
              </button>
              <span className="comm-thread-score">{thread.upvotes}</span>
              <button className="comm-thread-vote-btn">
                <ChevronDown size={14} />
              </button>
            </div>

            {/* Thread Content */}
            <div className="comm-thread-main">
              <div className="comm-thread-header">
                {thread.isPinned && (
                  <span className="comm-thread-pin">
                    <Pin size={10} />
                    Pinned
                  </span>
                )}
                {thread.flair && (
                  <span className={`comm-thread-flair comm-flair--${thread.flair.color}`}>
                    {thread.flair.text}
                  </span>
                )}
              </div>
              <h4 className="comm-thread-title">{thread.title}</h4>
              <p className="comm-thread-preview">{thread.content}</p>
              <div className="comm-thread-meta">
                <div className="comm-thread-author">
                  <div className="comm-thread-avatar">{thread.author.avatar}</div>
                  <span>{thread.author.name}</span>
                  {thread.author.role === 'creator' && <Award size={10} className="comm-role-icon creator" />}
                  {thread.author.role === 'moderator' && <Shield size={10} className="comm-role-icon mod" />}
                  {thread.author.role === 'bot' && <Zap size={10} className="comm-role-icon bot" />}
                </div>
                <span className="comm-thread-time">{thread.timeAgo}</span>
                <span className="comm-thread-comments">
                  <MessageCircle size={10} />
                  {thread.comments}
                </span>
              </div>

              {/* Thread Replies */}
              {thread.replies && thread.replies.length > 0 && (
                <div className="comm-thread-replies">
                  {thread.replies.map((reply, replyIndex) => (
                    <div key={replyIndex} className="comm-thread-reply">
                      <div className="comm-reply-line"></div>
                      <div className="comm-reply-content">
                        <div className="comm-reply-header">
                          <div className="comm-reply-avatar">{reply.author.avatar}</div>
                          <span className="comm-reply-author">{reply.author.name}</span>
                          {reply.author.role === 'creator' && <Award size={9} className="comm-role-icon creator" />}
                          {reply.author.role === 'moderator' && <Shield size={9} className="comm-role-icon mod" />}
                          <span className="comm-reply-time">{reply.timeAgo}</span>
                        </div>
                        <p className="comm-reply-text">{reply.content}</p>
                        <div className="comm-reply-actions">
                          <button className="comm-reply-vote">
                            <ChevronUp size={12} />
                            <span>{reply.upvotes}</span>
                          </button>
                          <button className="comm-reply-btn">
                            <MessageCircle size={10} />
                            Reply
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // ========================================
  // RENDER: Members View
  // ========================================
  const renderMembersView = () => (
    <div className="comm-members">
      {/* Members Stats */}
      <div className="comm-members-stats">
        <div className="comm-members-stat">
          <Users size={14} />
          <div className="comm-members-stat-info">
            <span className="comm-members-stat-value">{membersData.stats.total.toLocaleString()}</span>
            <span className="comm-members-stat-label">Total Members</span>
          </div>
        </div>
        <div className="comm-members-stat online">
          <Activity size={14} />
          <div className="comm-members-stat-info">
            <span className="comm-members-stat-value">{membersData.stats.online}</span>
            <span className="comm-members-stat-label">Online Now</span>
          </div>
        </div>
        <div className="comm-members-stat">
          <UserPlus size={14} />
          <div className="comm-members-stat-info">
            <span className="comm-members-stat-value">+{membersData.stats.newThisWeek}</span>
            <span className="comm-members-stat-label">This Week</span>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="comm-members-search">
        <Search size={11} />
        <input type="text" placeholder="Search members..." />
      </div>

      {/* Creator */}
      <div className="comm-members-section">
        <h4 className="comm-members-section-title">
          <Award size={12} />
          Creator
        </h4>
        {membersData.creators.map((member, index) => (
          <div 
            key={index} 
            className={`comm-member-card creator ${index < visibleItems ? 'visible' : ''}`}
          >
            <div className="comm-member-avatar creator">{member.avatar}</div>
            <div className="comm-member-info">
              <span className="comm-member-name">{member.name}</span>
              <span className="comm-member-meta">
                {member.posts} posts • {member.reputation.toLocaleString()} rep
              </span>
            </div>
            <div className="comm-member-badge creator">
              <Award size={10} />
              Creator
            </div>
          </div>
        ))}
      </div>

      {/* Moderators */}
      <div className="comm-members-section">
        <h4 className="comm-members-section-title">
          <Shield size={12} />
          Moderators ({membersData.moderators.length})
        </h4>
        {membersData.moderators.map((member, index) => (
          <div 
            key={index} 
            className={`comm-member-card mod ${index + 1 < visibleItems ? 'visible' : ''}`}
            style={{ transitionDelay: `${(index + 1) * 60}ms` }}
          >
            <div className="comm-member-avatar mod">{member.avatar}</div>
            <div className="comm-member-info">
              <span className="comm-member-name">{member.name}</span>
              <span className="comm-member-meta">
                {member.posts} posts • {member.reputation.toLocaleString()} rep
              </span>
            </div>
            <div className="comm-member-badge mod">
              <Shield size={10} />
              Mod
            </div>
          </div>
        ))}
      </div>

      {/* Top Contributors */}
      <div className="comm-members-section">
        <h4 className="comm-members-section-title">
          <Star size={12} />
          Top Contributors
        </h4>
        {membersData.topContributors.slice(0, 3).map((member, index) => (
          <div 
            key={index} 
            className={`comm-member-card ${index + 4 < visibleItems ? 'visible' : ''}`}
            style={{ transitionDelay: `${(index + 4) * 60}ms` }}
          >
            <div className="comm-member-avatar">{member.avatar}</div>
            <div className="comm-member-info">
              <span className="comm-member-name">{member.name}</span>
              <span className="comm-member-meta">
                {member.posts} posts • {member.reputation.toLocaleString()} rep
              </span>
            </div>
            <span className="comm-member-contrib-badge">{member.badge}</span>
          </div>
        ))}
      </div>
    </div>
  );

  // ========================================
  // RENDER: Chat View
  // ========================================
  const renderChatView = () => (
    <div className="comm-chat">
      {/* Chat Header */}
      <div className="comm-chat-header">
        <div className="comm-chat-info">
          <MessageCircle size={14} />
          <span className="comm-chat-title">Community Chat</span>
        </div>
        <div className="comm-chat-online">
          <span className="comm-chat-online-dot"></span>
          <span>342 online</span>
        </div>
      </div>

      {/* Messages */}
      <div className="comm-chat-messages">
        {chatMessages.map((msg, index) => (
          <div 
            key={msg.id} 
            className={`comm-chat-message ${msg.isMe ? 'me' : ''} ${msg.isPinned ? 'pinned' : ''} ${index < visibleItems ? 'visible' : ''}`}
            style={{ transitionDelay: `${index * 80}ms` }}
          >
            {msg.isPinned && (
              <div className="comm-chat-pinned-label">
                <Pin size={9} />
                Pinned Message
              </div>
            )}
            {!msg.isMe && (
              <div className="comm-chat-msg-avatar">{msg.user.avatar}</div>
            )}
            <div className="comm-chat-msg-content">
              {!msg.isMe && (
                <div className="comm-chat-msg-header">
                  <span className="comm-chat-msg-name">{msg.user.name}</span>
                  {msg.user.role === 'creator' && <Award size={9} className="comm-chat-role creator" />}
                  {msg.user.role === 'moderator' && <Shield size={9} className="comm-chat-role mod" />}
                  <span className="comm-chat-msg-time">{msg.time}</span>
                </div>
              )}
              <div className="comm-chat-msg-bubble">
                <p>{msg.message}</p>
              </div>
              {msg.reactions && (
                <div className="comm-chat-reactions">
                  {msg.reactions.map((r, i) => (
                    <span key={i} className="comm-chat-reaction">{r.emoji} {r.count}</span>
                  ))}
                </div>
              )}
              {msg.isMe && <span className="comm-chat-msg-time me">{msg.time}</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Chat Input */}
      <div className="comm-chat-input">
        <button className="comm-chat-btn">
          <Paperclip size={12} />
        </button>
        <input type="text" placeholder="Type a message..." />
        <button className="comm-chat-btn">
          <Smile size={12} />
        </button>
        <button className="comm-chat-send">
          <Send size={12} />
        </button>
      </div>
    </div>
  );

  // ========================================
  // RENDER: About/Info View
  // ========================================
  const renderAboutView = () => (
    <div className="comm-about">
      {/* About Header */}
      <div className="comm-about-header">
        <div className="comm-about-avatar">AI</div>
        <div className="comm-about-info">
          <h3 className="comm-about-name">{communityAbout.name}</h3>
          <span className="comm-about-category">{communityAbout.category}</span>
        </div>
      </div>

      {/* Description */}
      <div className={`comm-about-section ${0 < visibleItems ? 'visible' : ''}`}>
        <h4 className="comm-about-section-title">
          <Info size={12} />
          About
        </h4>
        <p className="comm-about-desc">{communityAbout.description}</p>
        <div className="comm-about-created">
          <Calendar size={10} />
          Created {communityAbout.created}
        </div>
      </div>

      {/* Rules */}
      <div className={`comm-about-section ${1 < visibleItems ? 'visible' : ''}`} style={{ transitionDelay: '80ms' }}>
        <h4 className="comm-about-section-title">
          <Shield size={12} />
          Community Rules
        </h4>
        <div className="comm-about-rules">
          {communityAbout.rules.map((rule) => (
            <div key={rule.id} className="comm-about-rule">
              <span className="comm-rule-number">{rule.id}</span>
              <div className="comm-rule-content">
                <span className="comm-rule-title">{rule.title}</span>
                <span className="comm-rule-desc">{rule.desc}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Links */}
      <div className={`comm-about-section ${2 < visibleItems ? 'visible' : ''}`} style={{ transitionDelay: '160ms' }}>
        <h4 className="comm-about-section-title">
          <Link2 size={12} />
          Community Links
        </h4>
        <div className="comm-about-links">
          {communityAbout.links.map((link, i) => (
            <a key={i} href={link.url} className="comm-about-link">
              <link.icon size={12} />
              <span>{link.label}</span>
              <ExternalLink size={10} />
            </a>
          ))}
        </div>
      </div>
    </div>
  );

  // ========================================
  // RENDER: Analytics View
  // ========================================
  const renderAnalyticsView = () => (
    <div className="comm-analytics">
      {/* Overview Cards */}
      <div className="comm-analytics-overview">
        {analyticsData.overview.map((stat, index) => (
          <div 
            key={index} 
            className={`comm-analytics-card ${index < visibleItems ? 'visible' : ''}`}
            style={{ transitionDelay: `${index * 60}ms` }}
          >
            <div className="comm-analytics-icon">
              <stat.icon size={14} />
            </div>
            <div className="comm-analytics-info">
              <span className="comm-analytics-value">{stat.value}</span>
              <span className="comm-analytics-label">{stat.label}</span>
            </div>
            <span className={`comm-analytics-change ${stat.trend}`}>
              {stat.trend === 'up' ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
              {stat.change}
            </span>
          </div>
        ))}
      </div>

      {/* Growth Chart */}
      <div className={`comm-analytics-section ${4 < visibleItems ? 'visible' : ''}`} style={{ transitionDelay: '240ms' }}>
        <h4 className="comm-analytics-section-title">
          <BarChart3 size={12} />
          Member Growth (This Week)
        </h4>
        <div className="comm-analytics-chart">
          {analyticsData.growth.map((day, i) => (
            <div key={i} className="comm-chart-bar-container">
              <div 
                className="comm-chart-bar" 
                style={{ height: `${(day.members / 80) * 100}%` }}
              ></div>
              <span className="comm-chart-label">{day.day}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Top Posts */}
      <div className={`comm-analytics-section ${5 < visibleItems ? 'visible' : ''}`} style={{ transitionDelay: '300ms' }}>
        <h4 className="comm-analytics-section-title">
          <Star size={12} />
          Top Performing Posts
        </h4>
        <div className="comm-analytics-posts">
          {analyticsData.topPosts.map((post, i) => (
            <div key={i} className="comm-analytics-post">
              <span className="comm-analytics-post-rank">#{i + 1}</span>
              <div className="comm-analytics-post-info">
                <span className="comm-analytics-post-title">{post.title}</span>
                <span className="comm-analytics-post-stats">
                  {post.views} views • {post.engagement} engagement
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ========================================
  // RENDER: Interior View (Inside Community) - No Header
  // ========================================
  const renderInteriorView = () => (
    <div className="comm-interior no-header">
      {/* Tab Content - Full Height */}
      <div className="comm-interior-content">
        {activeInteriorTab === 'feed' && renderUnifiedFeed()}
        {activeInteriorTab === 'threads' && renderThreadsView()}
        {activeInteriorTab === 'members' && renderMembersView()}
        {activeInteriorTab === 'chat' && renderChatView()}
      </div>
    </div>
  );

  // ========================================
  // MAIN RENDER - Always show interior view
  // ========================================
  return (
    <div className="comm-mockup-content">
      {renderInteriorView()}
    </div>
  );
};

export default CommunitiesMockupContent;
